import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useWebSocket, CONNECTION_STATUS } from '../context/WebSocketContext';
import BackgroundProcessingService from '../services/BackgroundProcessingService';
import ConnectionDebugger from './ConnectionDebugger';
import LogMonitor from './LogMonitor/LogMonitor';
import SystemEventVisualizer from './SystemEventVisualizer';
import { ProcessVisualizer } from './Process';
import { ProcessProvider } from '../context/ProcessContext';

// Import icons
import { ReactComponent as DashboardIcon } from '../assets/icons/dashboard.svg';
import { ReactComponent as SystemIcon } from '../assets/icons/system.svg';
import { ReactComponent as ProcessIcon } from '../assets/icons/process.svg';
import { ReactComponent as LogIcon } from '../assets/icons/log.svg';
import { ReactComponent as ServiceIcon } from '../assets/icons/service.svg';
import { ReactComponent as ToggleIcon } from '../assets/icons/toggle.svg';

function Dashboard() {
  const { serverData, userData, wsConnection } = useAuth();
  const { connectionStatus: wsStatus, connect, subscribe, subscribeToServerTopics, lastMessage } = useWebSocket();
  const [activeTab, setActiveTab] = useState(0);
  const [activePanel, setActivePanel] = useState('Dashboard');
  const [isPanelExpanded, setIsPanelExpanded] = useState(true);
  const [serverConnectionStatus, setServerConnectionStatus] = useState({});
  const [lastHeartbeat, setLastHeartbeat] = useState({});
  const [serverNameToIdMap, setServerNameToIdMap] = useState({});
  const [serverIdToNameMap, setServerIdToNameMap] = useState({});

  // Initialize connection status and heartbeat for each server
  useEffect(() => {
    if (serverData && serverData.length > 0) {
      const initialStatus = {};
      const initialHeartbeat = {};
      const nameToId = {};
      const idToName = {};
      
      // Get the current connection statuses from BackgroundProcessingService
      const backgroundStatuses = BackgroundProcessingService.getAllServerConnectionStatuses();
      
      serverData.forEach(server => {
        const serverId = server.server_id.toLowerCase(); // Normalize to lowercase for consistency
        const serverName = server.server_name;
        
        // Use status from background service if available, otherwise default to false
        initialStatus[serverId] = backgroundStatuses[serverId] === 'connected';
        
        // Get last heartbeat from background service if available
        const lastHeartbeatTime = BackgroundProcessingService.getLastHeartbeatTimestamp(serverId);
        initialHeartbeat[serverId] = lastHeartbeatTime || null;
        
        nameToId[serverName] = serverId;
        idToName[serverId] = serverName;
      });
      
      setServerConnectionStatus(initialStatus);
      setLastHeartbeat(initialHeartbeat);
      setServerNameToIdMap(nameToId);
      setServerIdToNameMap(idToName);
      
      if (serverData.length > 0 && activeTab >= serverData.length) {
        setActiveTab(0);
      }
      
      // Server mapping initialized silently
    }
  }, [serverData, activeTab]);
  
  // Monitor WebSocket connection status rather than triggering connections
  useEffect(() => {
    if (!wsConnection) {
      // Connection info missing - will be handled by error UI
    } else if (wsStatus === CONNECTION_STATUS.DISCONNECTED) {
      // WebSocket disconnected - will be managed by WebSocketContext
    } else if (wsStatus === CONNECTION_STATUS.CONNECTED) {
      // WebSocket connected - subscriptions handled by context
    } else if (wsStatus === CONNECTION_STATUS.ERROR) {
      console.log('%c Dashboard: WebSocket connection error', 'background: #F44336; color: white; padding: 2px 5px; border-radius: 3px;');
    }
  }, [wsConnection, wsStatus]);
  
  // Use BackgroundProcessingService to get the latest connection status and heartbeat timestamps
  useEffect(() => {
    // Function to sync with background service
    const syncWithBackgroundService = () => {
      if (serverData && serverData.length > 0) {
        // Get all server connection statuses from the background service
        const statuses = BackgroundProcessingService.getAllServerConnectionStatuses();
        let hasChanges = false;
        
        // Create new objects to update state once with all changes
        const newStatuses = {...serverConnectionStatus};
        const newHeartbeats = {...lastHeartbeat};
        
        // Update our local state based on the background service data
        serverData.forEach(server => {
          const serverId = server.server_id.toLowerCase();
          
          // Update connection status
          const isConnected = statuses[serverId] === 'connected';
          if (newStatuses[serverId] !== isConnected) {
            newStatuses[serverId] = isConnected;
            hasChanges = true;
          }
          
          // Update last heartbeat timestamp
          const lastHeartbeatTime = BackgroundProcessingService.getLastHeartbeatTimestamp(serverId);
          if (lastHeartbeatTime && newHeartbeats[serverId] !== lastHeartbeatTime) {
            newHeartbeats[serverId] = lastHeartbeatTime;
            hasChanges = true;
          }
        });
        
        // Only update state if there are changes
        if (hasChanges) {
          setServerConnectionStatus(newStatuses);
          setLastHeartbeat(newHeartbeats);
        }
      }
    };
    
    // Sync immediately when component mounts or when serverData changes
    syncWithBackgroundService();
    
    // Then set up interval for continuous updates
    const syncInterval = setInterval(syncWithBackgroundService, 500); // Check every 500ms for more responsiveness
    
    return () => clearInterval(syncInterval);
  }, [serverData, serverConnectionStatus, lastHeartbeat]);
  
  // Subscribe to topics is now handled directly in the WebSocketContext's onopen handler
  // The previous subscription logic has been removed to prevent duplicate subscriptions
  useEffect(() => {
    if (wsStatus === CONNECTION_STATUS.ERROR) {
      console.log('%c Dashboard: WebSocket connection error', 'background: #F44336; color: white; padding: 2px 5px; border-radius: 3px;');
    } else if (wsStatus === CONNECTION_STATUS.CONNECTED && serverData && serverData.length > 0) {
      console.log('Dashboard: WebSocket connected, subscriptions will be handled by WebSocketContext');
    }
  }, [wsStatus, serverData]);
  
  // State for storing different event types
  const [processEvents, setProcessEvents] = useState({});
  const [systemEvents, setSystemEvents] = useState({});
  const [logEvents, setLogEvents] = useState({});

  // Function to process heartbeat event and update server status
  const processHeartbeatEvent = useCallback((heartbeatData) => {
    if (!heartbeatData) return false;
    
    // Extract server_id from the heartbeat data
    let serverId = heartbeatData.server_id || null;
    
    if (!serverId) {
      console.warn('Invalid heartbeat data: missing server_id', heartbeatData);
      return false;
    }
    
    serverId = serverId.toLowerCase(); // Normalize to lowercase
    
    // Heartbeat received silently
    
    // Update last heartbeat timestamp
    setLastHeartbeat(prev => {
      const timestamp = Date.now();
      const updatedHeartbeats = {
        ...prev,
        [serverId]: timestamp
      };
      // Update lastHeartbeat state silently
      return updatedHeartbeats;
    });
    
    // Mark server as connected
    setServerConnectionStatus(prev => {
      // Only update if server was previously disconnected
      if (prev[serverId] === false) {
        const updated = {
          ...prev,
          [serverId]: true
        };
        console.log('Server reconnected:', serverId);
        return updated;
      }
      return prev;
    });
    
    return true;
  }, [serverNameToIdMap, serverIdToNameMap]);

  // Update lastHeartbeat and server connection status based on incoming events
  useEffect(() => {
    if (lastMessage && serverData) {
      // Check if the message is in the new format (with type and data properties)
      if (lastMessage.type && lastMessage.data) {
        // Process different event types - using lowercase type values from WebSocketContext
        switch (lastMessage.type) {
          case 'process': // Lowercase to match WebSocketContext
            if (lastMessage.data.event_name === 'Process') {
              const processEvent = lastMessage.data.Process;
              setProcessEvents(prev => ({
                ...prev,
                [processEvent.id]: processEvent
              }));
            }
            break;
            
          case 'systemevent': // Lowercase to match WebSocketContext
            if (lastMessage.data.event_name === 'SystemEvent') {
              const systemEvent = lastMessage.data.SystemEvent;
              setSystemEvents(prev => ({
                ...prev,
                [systemEvent.id]: systemEvent
              }));
            }
            break;
            
          case 'log': // Lowercase to match WebSocketContext
            if (lastMessage.data.event_name === 'Log') {
              const logEvent = lastMessage.data.Log;
              setLogEvents(prev => ({
                ...prev,
                [logEvent.id]: logEvent
              }));
            }
            break;
            
          case 'heartbeat': // Lowercase to match WebSocketContext
            // Process heartbeat in the new format
            if (lastMessage.data.event_name === 'Heartbeat') {
              processHeartbeatEvent(lastMessage.data.Heartbeat);
            }
            break;
            
          default:
            console.log('Dashboard: Unknown event type:', lastMessage.type, lastMessage.data);
        }
        
        // Also check for Heartbeat events in the message data regardless of type
        // This handles cases where the event type might be misclassified
        if (lastMessage.data.event_name === 'Heartbeat' && !lastMessage.data.processed) {
          lastMessage.data.processed = true; // Mark as processed to avoid double processing
          processHeartbeatEvent(lastMessage.data);
        }
      } else {
        // Handle legacy format (direct message structure)
        if (lastMessage.event_name === 'Heartbeat') {
          processHeartbeatEvent(lastMessage);
        }
      }
    }
  }, [lastMessage, serverData, processHeartbeatEvent]);


  // Periodically check for heartbeat timeouts
  useEffect(() => {
    const HEARTBEAT_TIMEOUT_MS = 30000; // 30 seconds timeout
    const CHECK_INTERVAL_MS = 5000;     // Check every 5 seconds
    
    const interval = setInterval(() => {
      const now = Date.now();
      
      setServerConnectionStatus(prevStatus => {
        let hasChanges = false;
        const updatedStatus = { ...prevStatus };
        
        Object.keys(prevStatus).forEach(serverId => {
          const last = lastHeartbeat[serverId];
          const serverName = serverIdToNameMap[serverId] || 'Unknown';
          const timeSinceLastHeartbeat = last ? (now - last) : null;
          const isTimeout = !last || (timeSinceLastHeartbeat > HEARTBEAT_TIMEOUT_MS);
          
          // Server status needs to change from connected to disconnected
          if (isTimeout && updatedStatus[serverId] === true) {
            console.log(
              '%c Server disconnected:', 
              'background: #F44336; color: white; padding: 2px 5px; border-radius: 3px;',
              `Server ID: ${serverId}, Server Name: ${serverName}, ` +
              `Last heartbeat: ${last ? new Date(last).toISOString() : 'never'}, ` +
              `Time since last: ${last ? (timeSinceLastHeartbeat / 1000).toFixed(1) : 'N/A'} seconds ` +
              `(timeout: ${HEARTBEAT_TIMEOUT_MS / 1000}s)`
            );
            updatedStatus[serverId] = false;
            hasChanges = true;
          } 
          // Server is connected and still receiving heartbeats - only log occasionally to reduce spam
          else if (!isTimeout && last && (now % 30000 < CHECK_INTERVAL_MS)) {
            console.log(
              `Server ID: ${serverId}, Server Name: ${serverName}, Connected: ${updatedStatus[serverId]}, ` +
              `Last heartbeat: ${new Date(last).toISOString()}, ` +
              `Time since last: ${(timeSinceLastHeartbeat / 1000).toFixed(1)} seconds`
            );
          }
        });
        
        // Only return a new object if there were changes (to avoid unnecessary re-renders)
        return hasChanges ? updatedStatus : prevStatus;
      });
    }, CHECK_INTERVAL_MS);
    
    return () => clearInterval(interval);
  }, [lastHeartbeat, serverIdToNameMap]);



  // If no server data is available
  if (!serverData || serverData.length === 0) {
    return (
      <div className="component-container">
        <h1>Dashboard Overview</h1>
        <p>No server data available. Please check your configuration.</p>
      </div>
    );
  }

  // Event panel types available in the system with their icons
  const eventPanelTypes = [
    { name: 'Dashboard', icon: DashboardIcon },
    { name: 'SystemEvent', icon: SystemIcon },
    { name: 'Process', icon: ProcessIcon },
    { name: 'Log', icon: LogIcon },
    { name: 'Service', icon: ServiceIcon }
  ];

  /**
   * Renders the content for the selected panel type
   * @returns {JSX.Element} The panel content
   */
  const renderPanelContent = () => {
    // If no server is selected, show a message
    if (!serverData || !serverData[activeTab]) {
      return (
        <div className="panel-content">
  
          <p>Please select a server from the tabs above.</p>
        </div>
      );
    }

    const currentServer = serverData[activeTab];
    const serverId = currentServer.server_id.toLowerCase();
    
    switch (activePanel) {
      case 'Dashboard':
        return (
          <div className="panel-content">
            <p>Overview dashboard for the selected server.</p>
            <div className="placeholder-content">
              <p>Server ID: {currentServer.server_id}</p>
              <p>Server Name: {currentServer.server_name}</p>
              <p>Server Status: 
                <span className={`status-text ${serverConnectionStatus[serverId] ? 'connected-text' : 'disconnected-text'}`}>
                  {serverConnectionStatus[serverId] ? 'Connected' : 'Disconnected'}
                </span>
              </p>
              <p>Last Heartbeat: {lastHeartbeat[serverId] ? 
                new Date(lastHeartbeat[serverId]).toLocaleString() : 'Never'}</p>
              <p>WebSocket Status: {wsStatus}</p>
            </div>
          </div>
        );
      case 'SystemEvent':
        return (
          <div className="panel-content system-panel">
            <SystemEventVisualizer serverId={serverData[activeTab]?.server_id} />
          </div>
        );
      case 'Process':
        return (
          <div className="panel-content">
            <ProcessProvider>
              <ProcessVisualizer serverId={serverData[activeTab]?.server_id} />
            </ProcessProvider>
          </div>
        );
      case 'Log':
        return (
          <div className="panel-content">
  
            <LogMonitor serverId={serverData[activeTab]?.server_id} />
          </div>
        );
      case 'Service':
        return (
          <div className="panel-content">
  
            <p>This panel will display service information (To be implemented later).</p>
          </div>
        );
      default:
        return (
          <div className="panel-content">
  
            <p>The selected panel type is not recognized.</p>
          </div>
        );
    }
  };

  return (
    <div className="dashboard-container">
      {/* Connection Debugger - only shown in development mode */}
      {process.env.NODE_ENV === 'development' && <ConnectionDebugger />}
      
      {/* Server Tabs */}
      <div className="server-tabs">
        {serverData.map((server, index) => (
          <div 
            key={server.server_id} 
            className={`server-tab ${activeTab === index ? 'active' : ''}`}
            onClick={() => setActiveTab(index)}
          >
            <span>{server.server_name}</span>
            <span 
              className={`connection-indicator ${serverConnectionStatus[server.server_id.toLowerCase()] ? 'connected' : 'disconnected'}`}
              title={serverConnectionStatus[server.server_id.toLowerCase()] ? 
                `Connected - Last heartbeat: ${lastHeartbeat[server.server_id.toLowerCase()] ? new Date(lastHeartbeat[server.server_id.toLowerCase()]).toLocaleTimeString() : 'N/A'}` : 
                'Disconnected - No recent heartbeat'}
            ></span>
          </div>
        ))}
      </div>

      {/* Main Content Area with Left Panel and Content */}
      <div className="main-content">
        {serverData[activeTab] && (
          <>
            {/* Left Panel - Event Type Navigation */}
            <div className={`left-panel ${isPanelExpanded ? 'expanded' : 'collapsed'}`}>
              <div className="panel-header">
                <button 
                  className="toggle-panel-btn"
                  onClick={() => setIsPanelExpanded(!isPanelExpanded)}
                  title={isPanelExpanded ? 'Collapse panel' : 'Expand panel'}
                >
                  <ToggleIcon />
                </button>
              </div>
              <ul className="event-type-list">
                {eventPanelTypes.map((panel) => (
                  <li 
                    key={panel.name}
                    className={`event-type-item ${activePanel === panel.name ? 'active' : ''}`}
                    onClick={() => setActivePanel(panel.name)}
                    title={panel.name}
                  >
                    <span className="event-icon">
                      <panel.icon />
                    </span>
                    {isPanelExpanded && <span className="event-name">{panel.name}</span>}
                  </li>
                ))}
              </ul>
            </div>

            {/* Right Content Panel */}
            <div className={`content-panel ${!isPanelExpanded ? 'expanded-content' : ''}`}>
              <h2>{serverData[activeTab].server_name} - {activePanel}</h2>
              {renderPanelContent()}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
